import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Building2, Users, Calendar } from 'lucide-react';
import api from '../api';
import AlertModal from '../components/AlertModal';
import SEO from '../components/SEO';

export default function ContactPage() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        organization: '',
        subject: '',
        message: '',
        inquiryType: 'general'
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await api.submitContact({
                inquiry_type: formData.inquiryType,
                name: formData.name,
                email: formData.email,
                organization: formData.organization || null,
                subject: formData.subject,
                message: formData.message
            });

            setSubmitted(true);

            // Reset form after 3 seconds
            setTimeout(() => {
                setSubmitted(false);
                setFormData({
                    name: '',
                    email: '',
                    organization: '',
                    subject: '',
                    message: '',
                    inquiryType: 'general'
                });
            }, 3000);
        } catch (err) {
            console.error('Contact form error:', err);
            alert(err.message || 'Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const contactInfo = [
        {
            icon: MapPin,
            title: 'Address',
            details: ['1 AMK Industrial Park 2A', '#06-06 AMK Tech 1', 'Singapore 568049'],
            color: '#06b6d4'
        },
        {
            icon: Mail,
            title: 'Email',
            details: ['admin@atam.sg'],
            link: 'mailto:admin@atam.sg',
            color: '#f43f5e'
        },
        {
            icon: Phone,
            title: 'Phone',
            details: ['+65 8932 7048'],
            link: 'tel:+6589327048',
            color: '#a855f7'
        }
    ];

    const inquiryTypes = [
        {
            value: 'general',
            label: 'General Inquiry',
            icon: Mail,
            color: '#06b6d4'
        },
        {
            value: 'partnership',
            label: 'Partnership Opportunity',
            icon: Building2,
            color: '#f43f5e'
        },
        {
            value: 'event',
            label: 'Submit an Event',
            icon: Calendar,
            color: '#a855f7'
        },
        {
            value: 'community',
            label: 'Community Support',
            icon: Users,
            color: '#f97316'
        }
    ];

    return (
        <main className="main">
            <SEO
                title="Contact Us"
                description="Get in touch with ATAM Connect. Have questions or want to partner with us? Reach out and let's build something amazing together."
                keywords="contact ATAM, partner with us, Singapore cultural events, event partnership, arts collaboration"
            />
            {/* Hero Section */}
            <section className="hero contact-hero" style={{
                minHeight: '400px',
                background: 'linear-gradient(135deg, rgba(222, 65, 176, 0.9) 0%, rgba(254, 205, 109, 0.9) 50%, rgba(255, 252, 242, 0.9) 100%)'
            }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h1 style={{ fontSize: '48px', marginBottom: '24px' }}>Get In Touch</h1>
                    <p style={{
                        fontSize: '18px',
                        maxWidth: '700px',
                        margin: '0 auto',
                        color: '#212121'
                    }}>
                        Have questions? Want to partner with us? We'd love to hear from you.
                        Reach out and let's build something amazing together.
                    </p>
                </div>
            </section>

            {/* Contact Info Cards */}
            <section className="contact-info-section" style={{
                padding: '64px 0',
                background: 'var(--bg-secondary)',
                borderTop: '1px solid var(--border)'
            }}>
                <div className="container">
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '24px'
                    }}>
                        {contactInfo.map((info) => (
                            <div
                                key={info.title}
                                style={{
                                    background: 'white',
                                    borderRadius: '24px',
                                    padding: '36px',
                                    border: '1px solid rgba(0, 0, 0, 0.06)',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                                    transition: 'all 0.3s'
                                }}
                            >
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '16px',
                                    background: '#F0F4F8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '20px'
                                }}>
                                    <info.icon size={28} strokeWidth={1.5} />
                                </div>
                                <h3 style={{
                                    fontSize: '22px',
                                    fontWeight: '600',
                                    marginBottom: '12px'
                                }}>
                                    {info.title}
                                </h3>
                                {info.link ? (
                                    <a
                                        href={info.link}
                                        style={{
                                            color: '#4B5563',
                                            fontSize: '16px',
                                            lineHeight: '1.7',
                                            textDecoration: 'none',
                                            transition: 'color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.color = info.color}
                                        onMouseLeave={(e) => e.target.style.color = '#4B5563'}
                                    >
                                        {info.details[0]}
                                    </a>
                                ) : (
                                    info.details.map((detail, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                color: '#4B5563',
                                                fontSize: '16px',
                                                lineHeight: '1.7',
                                                marginBottom: '4px'
                                            }}
                                        >
                                            {detail}
                                        </div>
                                    ))
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form */}
            <section className="contact-form-section" style={{ padding: '80px 0' }}>
                <div className="container">
                    <div className="contact-form-container" style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <h2 style={{
                            fontSize: '36px',
                            fontWeight: '700',
                            marginBottom: '16px',
                            textAlign: 'center'
                        }}>
                            Send Us a Message
                        </h2>
                        <p style={{
                            textAlign: 'center',
                            color: 'var(--text-secondary)',
                            marginBottom: '48px',
                            fontSize: '16px'
                        }}>
                            Fill out the form below and we'll get back to you as soon as possible
                        </p>

                        {submitted ? (
                            <div className="card" style={{
                                padding: '48px',
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, #10b98120, #06b6d420)',
                                border: '2px solid #10b981'
                            }}>
                                <CheckCircle size={64} style={{ color: '#10b981', margin: '0 auto 24px' }} />
                                <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '12px', color: '#10b981' }}>
                                    Message Sent Successfully!
                                </h3>
                                <p style={{ color: 'var(--text-secondary)' }}>
                                    Thank you for reaching out. We'll get back to you within 24-48 hours.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="card contact-form-card" style={{ padding: '40px' }}>
                                {/* Inquiry Type Selection */}
                                <div style={{ marginBottom: '32px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '12px',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}>
                                        What can we help you with?
                                    </label>
                                    <div className="inquiry-grid" style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: '12px'
                                    }}>
                                        {inquiryTypes.map((type) => (
                                            <label
                                                key={type.value}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '12px 16px',
                                                    border: `2px solid ${formData.inquiryType === type.value ? type.color : 'var(--border)'}`,
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    background: formData.inquiryType === type.value ? `${type.color}10` : 'transparent',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="inquiryType"
                                                    value={type.value}
                                                    checked={formData.inquiryType === type.value}
                                                    onChange={handleChange}
                                                    style={{ display: 'none' }}
                                                />
                                                <type.icon size={18} style={{
                                                    color: formData.inquiryType === type.value ? type.color : 'var(--text-secondary)'
                                                }} />
                                                <span style={{
                                                    fontSize: '13px',
                                                    color: formData.inquiryType === type.value ? type.color : 'var(--text-primary)'
                                                }}>
                                                    {type.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Name */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}>
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="input"
                                        placeholder="Your full name"
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                {/* Email */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}>
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="input"
                                        placeholder="your.email@example.com"
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                {/* Organization */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}>
                                        Organization (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        name="organization"
                                        value={formData.organization}
                                        onChange={handleChange}
                                        className="input"
                                        placeholder="Your organization name"
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                {/* Subject */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}>
                                        Subject *
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        required
                                        className="input"
                                        placeholder="Brief subject line"
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                {/* Message */}
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}>
                                        Message *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        className="input"
                                        rows="6"
                                        placeholder="Tell us more about your inquiry..."
                                        style={{ resize: 'vertical', minHeight: '120px', width: '100%' }}
                                    />
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                    style={{
                                        width: '100%',
                                        fontSize: '16px',
                                        padding: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    {loading ? 'Sending...' : (
                                        <>
                                            <Send size={18} />
                                            Send Message
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="map-section" style={{
                padding: '0 0 64px 0'
            }}>
                <div className="container">
                    <div className="card" style={{
                        padding: '0',
                        overflow: 'hidden',
                        height: '450px',
                        borderRadius: '24px'
                    }}>
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.6558894847543!2d103.85283247587456!3d1.3779839986252283!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da16e54f3a85a7%3A0x72c6fb2e93713c94!2s1%20AMK%20Industrial%20Park%202A%2C%20%2306-06%20AMK%20Tech%201%2C%20Singapore%20568049!5e0!3m2!1sen!2ssg!4v1737013000000!5m2!1sen!2ssg"
                            width="100%"
                            height="450"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="ATAM Location Map"
                        />
                    </div>
                </div>
            </section>
        </main>
    );
}
